import {Injectable, NgZone} from '@angular/core';
import {SessionProvider} from '../model/SessionProvider';
import {StreamControllerService, StreamEventConfigDto, UserDto} from '@api/utisha-backend';
import {StreamVideoClient} from '@stream-io/video-client';
import {AsyncSessionResult, SessionResult} from '../model/AsyncSessionResult';
import {SessionInitializationOptions} from '../model/SessionInitializationOptions';
import {SupportedVideoElementType} from '../model/SessionState';
import {getDefaultCapabilities, SessionCapabilities} from '../model/SessionCapabilities';
import {StreamSessionMedia} from './StreamSessionMedia';
import {firstValueFrom} from 'rxjs';
import {SessionCommand} from '../model/SessionCommand';
import {logUtishaDebug, logUtishaError, logUtishaInfo} from 'src/app/util/log';
import {UtishaGeneralStore} from '../../../services/storage/general-store';
import * as Sentry from '@sentry/angular';
import {SessionStateMachine} from '../model/SessionStateMachine';
import {SessionStore} from '../model/SessionStore';
import {Operation, SessionOperationQueue} from '../model/SessionOperationQueue';
import {SubscriptionManager} from '../model/SubscriptionManager';
import {SessionCommands} from '../model/SessionCommands';

@Injectable({
  providedIn: 'root',
})
export class StreamSessionProvider implements SessionProvider {
  private client: StreamVideoClient | null = null;
  private media: StreamSessionMedia | null = null;
  private streamSessionId: string = '';
  private eventId: number = 0;
  private streamConfig: StreamEventConfigDto | null = null;
  private tokenProvider: () => Promise<string>;

  private stateMachine: SessionStateMachine;
  private store: SessionStore;
  private operationQueue: SessionOperationQueue;
  private subscriptions: SubscriptionManager;

  private commands: SessionCommands | null = null;

  constructor(
    private ngZone: NgZone,
    private streamService: StreamControllerService,
    private utishaGeneralStore: UtishaGeneralStore
  ) {
    this.stateMachine = new SessionStateMachine();
    this.store = new SessionStore();
    this.operationQueue = new SessionOperationQueue(this.stateMachine);
    this.subscriptions = new SubscriptionManager();
    this.tokenProvider = async () => {
      if (!this.eventId) throw new Error('Event ID not set');
      return await firstValueFrom(this.streamService.getCallToken(this.eventId));
    };

    this.setupStateSubscriptions();
  }

  private setupStateSubscriptions(): void {
    this.subscriptions.add(
      this.stateMachine.state$.subscribe((state: import('../model/SessionStateMachine').SessionState) => {
        logUtishaDebug(`Session state changed: ${state.status}`);

        this.store.update({
          session: {
            isJoined: state.status === 'joined',
            isEnded: state.status === 'left' || state.status === 'error',
            error: state.status === 'error' ? (state as any).error.message : null,
            capabilities: this.getCapabilities()
          }
        });

        if (state.status === 'error') {
          logUtishaError('Session error:', (state as any).error);
        }
      }),
      'stateMachine'
    );
  }

  async initialize(commands: SessionCommands, store: SessionStore, config?: SessionInitializationOptions): AsyncSessionResult {
    this.commands = commands;

    if (!config?.eventId) {
      return SessionResult.failure('Event ID is required');
    }

    const operation: Operation<AsyncSessionResult> = {
      id: `initialize-${config.eventId}`,
      type: 'initialize',
      predicate: (state) => state.status === 'idle' || state.status === 'left' || state.status === 'error',
      execute: async (context) => {
        try {
          this.eventId = config.eventId!;

          const abortController = new AbortController();
          this.stateMachine.transition({
            status: 'initializing',
            abortController,
            eventId: this.eventId
          });

          if (context.signal.aborted) {
            throw new Error('Initialization aborted');
          }

          this.streamConfig = await firstValueFrom(
            this.streamService.getConfig(config.eventId!)
          );

          if (context.signal.aborted) {
            throw new Error('Initialization aborted after config fetch');
          }

          const {apiKey, userId} = this.streamConfig;

          this.client = new StreamVideoClient({
            apiKey: apiKey!,
            tokenProvider: this.tokenProvider,
            user: {id: userId!}
          });

          this.stateMachine.transition({
            status: 'initialized',
            client: this.client,
            eventId: this.eventId
          });

          logUtishaInfo(`Stream session initialized for event ${this.eventId}`);
          return SessionResult.ok;

        } catch (error: any) {
          this.stateMachine.transitionToError(error);
          logUtishaError('Error initializing Stream.io client:', error);
          return SessionResult.failure('Failed to initialize Stream.io client');
        }
      }
    };

    return this.operationQueue.enqueue(operation);
  }

  async join(eventId: number, user: UserDto): AsyncSessionResult {
    const operation: Operation<AsyncSessionResult> = {
      id: `join-${eventId}`,
      type: 'join',
      predicate: (state) => state.status === 'initialized',
      execute: async (context) => {
        return this.ngZone.runOutsideAngular(async () => {
          try {
            if (!this.client) {
              throw new Error('Client not initialized');
            }

            logUtishaDebug(`Joining Stream.io session for event ${eventId} and user ${user.id}`);

            this.streamSessionId = `event-${eventId}`;
            const call = this.client.call('default', this.streamSessionId);

            this.stateMachine.transition({
              status: 'joining',
              client: this.client,
              call,
              eventId
            });

            if (context.signal.aborted) {
              throw new Error('Join aborted');
            }

            await call.join({create: true});

            if (context.signal.aborted) {
              await call.leave();
              throw new Error('Join aborted after call join');
            }

            this.media = new StreamSessionMedia(
              this.commands!,
              this.store,
              call,
              this.utishaGeneralStore,
              this.ngZone,
              this.stateMachine
            );
            await this.media.initialize();

            this.stateMachine.transition({
              status: 'joined',
              client: this.client,
              call,
              media: this.media,
              eventId,
              streamSessionId: this.streamSessionId
            });

            this.store.update({
              session: {
                isJoined: true,
                capabilities: this.getCapabilities()
              },
              localUser: {
                id: parseInt(user.id!.toString())
              }
            });

            Sentry.setTag('callId', this.streamSessionId);
            Sentry.setTag('eventId', `${eventId}`);

            logUtishaInfo(`Successfully joined session ${this.streamSessionId}`);
            return SessionResult.ok;

          } catch (error: any) {
            this.stateMachine.transitionToError(error);
            logUtishaError('Error joining Stream.io session:', error);
            return SessionResult.failure('Failed to join Stream.io session');
          }
        });
      }
    };

    return this.operationQueue.enqueue(operation);
  }

  async leave(end?: boolean): AsyncSessionResult {
    const operation: Operation<AsyncSessionResult> = {
      id: `leave-${end ? 'end' : 'leave'}-${Date.now()}`,
      type: 'leave',
      predicate: (state) => ['joined', 'joining', 'error'].includes(state.status),
      execute: async (context) => {
        try {
          const currentState = this.stateMachine.currentState;

          if (currentState.status !== 'joined' && currentState.status !== 'joining') {
            logUtishaDebug(`Cannot leave from state: ${currentState.status}`);
            return SessionResult.ok;
          }

          this.stateMachine.transition({
            status: 'leaving',
            reason: end ? 'ended' : 'user',
            previousState: currentState as any
          });

          if (context.signal.aborted) {
            throw new Error('Leave operation aborted');
          }

          if (this.media) {
            await this.media.destroy();
            this.media = null;
          }

          if (context.signal.aborted) {
            throw new Error('Leave operation aborted during media cleanup');
          }

          const call = currentState.status === 'joined' ? (currentState as any).call : null;
          if (call) {
            if (end) {
              await call.endCall();
              this.store.update({ session: { isEnded: true } });
            } else {
              await call.leave();
              this.store.update({ session: { isEnded: false } });
            }
          }

          if (context.signal.aborted) {
            throw new Error('Leave operation aborted after call cleanup');
          }

          if (this.client) {
            await this.client.disconnectUser();
            this.client = null;
          }

          this.stateMachine.transition({
            status: 'left',
            reason: end ? 'ended' : 'user'
          });

          Sentry.setTag('callId', undefined);
          Sentry.setTag('eventId', undefined);

          logUtishaInfo(`Session ${end ? 'ended' : 'left'} successfully`);
          return SessionResult.ok;

        } catch (error: any) {
          logUtishaError('Error leaving Stream.io session:', error);
          this.stateMachine.transitionToError(error);
          return SessionResult.failure('Failed to leave Stream.io session');
        }
      }
    };

    return this.operationQueue.enqueue(operation);
  }

  async end(): AsyncSessionResult {
    return this.leave(true);
  }

  async sendCommand(command: SessionCommand): AsyncSessionResult {
    const currentState = this.stateMachine.currentState;

    if (currentState.status !== 'joined') {
      return SessionResult.failure('Cannot send command: not in joined state');
    }

    try {
      if (this.media?.call) {
        await this.media.call.sendCustomEvent({type: 'command', data: command});
      }
      this.store.update({ ui: { command } });
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error sending command:', error);
      return SessionResult.failure('Failed to send command');
    }
  }

  getCapabilities(): SessionCapabilities {
    return this.store.snapshot.session.capabilities || getDefaultCapabilities();
  }

  getSupportedVideoElementType(): SupportedVideoElementType {
    return 'video';
  }

  async printSessionSummary(): Promise<void> {
    try {
      const currentState = this.stateMachine.currentState;
      const isConnected = currentState.status === 'joined';

      let summary = `=== Stream Session Summary ===
Session ID: ${this.streamSessionId || 'Not connected'}
Event ID: ${this.eventId || 'Not set'}
State: ${currentState.status}
Connection Status: ${isConnected ? 'Connected' : 'Disconnected'}`;

      if (isConnected && currentState.status === 'joined') {
        const joinedState = currentState as any;
        const callState = joinedState.call.state;
        const participantsList = Array.from(callState.participants.entries())
          .map((entry: any) => {
            const participant = entry[1];
            return `  - [${participant.userId}] ${participant.name || 'Unknown'} (${participant.roles})`;
          })
          .join('\n');

        summary += `
Call Status: ${callState.callingState ? 'In progress' : 'Not active'}
Call Created At: ${callState.createdAt.toISOString()}
Participants: ${callState.participants.length}
Camera Status: ${joinedState.call.camera.enabled ? 'Enabled' : 'Disabled'}
Microphone Status: ${joinedState.call.microphone.enabled ? 'Enabled' : 'Disabled'}
Apikey: ${this.streamConfig?.apiKey?.slice(0, 8) + '...'}
Participants:
${participantsList}`;
      }

      summary += `
Session Capabilities: ${JSON.stringify(this.getCapabilities(), null, 2)}
==============================`;

      logUtishaInfo(summary);
    } catch (error: any) {
      logUtishaError('Error printing session summary:', error);
    }
  }

  getStateMachine(): SessionStateMachine {
    return this.stateMachine;
  }

  getStore(): SessionStore {
    return this.store;
  }

  destroy(): void {
    this.operationQueue.cancelAll('Provider destroyed');
    this.store.reset();
    this.subscriptions.unsubscribeAll();
    this.commands = null;

    if (this.media) {
      this.media.destroy();
      this.media = null;
    }

    if (this.client) {
      this.client.disconnectUser().catch(() => {});
      this.client = null;
    }
  }
}
